import { CheckSquare } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FluentCard } from '@/components/layout/page-header'
import type { FieldCompletionData } from '@/lib/api/types'

interface FieldCompletionChartProps {
  data: FieldCompletionData[]
  loading?: boolean
}

export function FieldCompletionChart({ data, loading }: FieldCompletionChartProps) {
  const getCompletionColor = (rate: number) => {
    if (rate >= 80) return 'bg-green-500'
    if (rate >= 50) return 'bg-amber-500'
    return 'bg-red-500'
  }

  const getCompletionBgColor = (rate: number) => {
    if (rate >= 80) return 'bg-green-100 dark:bg-green-900/30'
    if (rate >= 50) return 'bg-amber-100 dark:bg-amber-900/30'
    return 'bg-red-100 dark:bg-red-900/30'
  }

  const getCompletionTextColor = (rate: number) => {
    if (rate >= 80) return 'text-green-700 dark:text-green-400'
    if (rate >= 50) return 'text-amber-700 dark:text-amber-400'
    return 'text-red-700 dark:text-red-400'
  }

  // Sort by completion rate (lowest first to highlight problem areas)
  const sortedData = [...data].sort((a, b) => a.completionRate - b.completionRate)

  if (loading) {
    return (
      <FluentCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-cyan-400 to-teal-600">
            <CheckSquare className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Field Completion Rates
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              How often each field is filled in
            </p>
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="flex-1 h-3 bg-gray-200 dark:bg-gray-700 rounded-full" />
              <div className="w-12 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      </FluentCard>
    )
  }

  // Calculate average completion rate
  const avgCompletion =
    data.length > 0
      ? data.reduce((acc, d) => acc + d.completionRate, 0) / data.length
      : 0

  return (
    <FluentCard>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-cyan-400 to-teal-600">
            <CheckSquare className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Field Completion Rates
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              How often each field is filled in
            </p>
          </div>
        </div>

        {/* Average badge */}
        {data.length > 0 && (
          <div
            className={cn(
              'px-3 py-1.5 rounded-full text-sm font-medium',
              getCompletionBgColor(avgCompletion),
              getCompletionTextColor(avgCompletion)
            )}
          >
            Avg: {avgCompletion.toFixed(1)}%
          </div>
        )}
      </div>

      {data.length > 0 ? (
        <div className="space-y-4">
          {sortedData.map((field) => (
            <div key={field.fieldName} className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700 dark:text-gray-300 truncate max-w-[60%]">
                  {field.fieldName}
                </span>
                <span
                  className={cn(
                    'font-semibold',
                    getCompletionTextColor(field.completionRate)
                  )}
                >
                  {field.completionRate.toFixed(1)}%
                </span>
              </div>
              <div className="h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    getCompletionColor(field.completionRate)
                  )}
                  style={{ width: `${field.completionRate}%` }}
                />
              </div>
            </div>
          ))}

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-xs">
              <span className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-gray-500 dark:text-gray-400">&lt; 50% (Low)</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="w-3 h-3 rounded-full bg-amber-500" />
              <span className="text-gray-500 dark:text-gray-400">50-80% (Medium)</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-gray-500 dark:text-gray-400">&ge; 80% (High)</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <CheckSquare className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No field completion data available
          </p>
        </div>
      )}
    </FluentCard>
  )
}
